
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function CaseStudiesPage() {
  const caseStudies = [
    {
      title: 'WellnessPro: 300% Lead Increase',
      industry: 'Health & Wellness',
      challenge: 'Struggling to convert website visitors into booked consultations',
      solution: 'Implemented Lead Generation A.I. with automated qualification and booking',
      results: [
        '300% increase in qualified leads',
        '85% reduction in manual follow-up time',
        '$150K additional revenue in 6 months'
      ],
      testimonial: "Talynt AI transformed our lead generation process. We're now booking 3x more qualified appointments with zero additional staff.",
      author: 'Sarah Chen, CEO',
      image: 'https://public.readdy.ai/ai/img_res/e509f2d3e3f960d21c74921e78e925ef.jpg'
    },
    {
      title: 'HomeFixPro: 20 Hours Saved Weekly',
      industry: 'Home Services',
      challenge: 'Overwhelmed with quote requests and administrative tasks',
      solution: 'Deployed Process Automation A.I. for quotes, scheduling, and billing',
      results: [
        '20 hours saved per week on admin',
        '60% faster quote delivery',
        '40% increase in customer satisfaction'
      ],
      testimonial: "The automation saved us 20 hours per week on admin tasks. Our team can now focus on what matters most - serving our clients.",
      author: 'Michael Rodriguez, Founder',
      image: 'https://readdy.ai/api/search-image?query=Professional%20home%20services%20office%20with%20automated%20systems%2C%20modern%20contractor%20workspace%20with%20digital%20scheduling%20tools%2C%20home%20improvement%20business%20environment%2C%20automated%20quote%20generation%20displays&width=600&height=400&seq=homefix-case&orientation=landscape'
    },
    {
      title: 'FinanceFirst: 45% Retention Boost',
      industry: 'Financial Services',
      challenge: 'High client churn and lengthy onboarding process',
      solution: 'Implemented Client Experience A.I. with automated onboarding and retention',
      results: [
        '45% improvement in client retention',
        '75% reduction in onboarding time',
        '$200K increase in recurring revenue'
      ],
      testimonial: "Our client retention improved by 45% after implementing their A.I. client experience system. The ROI was immediate.",
      author: 'Jennifer Park, Managing Partner',
      image: 'https://readdy.ai/api/search-image?query=Modern%20financial%20services%20office%20with%20AI%20client%20management%20systems%2C%20professional%20banking%20environment%2C%20automated%20onboarding%20displays%2C%20financial%20consulting%20workspace%20with%20digital%20tools&width=600&height=400&seq=finance-case&orientation=landscape'
    },
    {
      title: 'LegalEase: 50% Efficiency Gain',
      industry: 'Professional Services',
      challenge: 'Manual document processing and client communication bottlenecks',
      solution: 'Deployed comprehensive A.I. automation for document handling and client management',
      results: [
        '50% increase in operational efficiency',
        '90% reduction in document processing time',
        '35% growth in client capacity'
      ],
      testimonial: "The A.I. system handles our entire document workflow. We can now serve 35% more clients with the same team size.",
      author: 'David Thompson, Senior Partner',
      image: 'https://readdy.ai/api/search-image?query=Professional%20law%20office%20with%20AI%20document%20management%20systems%2C%20modern%20legal%20workspace%20with%20automated%20filing%20systems%2C%20professional%20services%20environment%20with%20digital%20case%20management%20tools&width=600&height=400&seq=legal-case&orientation=landscape'
    }
  ];

  const metrics = [
    { value: '500+', label: 'Businesses Automated' },
    { value: '300%', label: 'Average Lead Increase' },
    { value: '25hrs', label: 'Weekly Time Saved' },
    { value: '$2.5M', label: 'Revenue Generated' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Real Results from <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">A.I. Automation</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
              Discover how businesses across industries are achieving remarkable growth with our A.I. solutions.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {metrics.map((metric, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{metric.value}</div>
                  <div className="text-gray-600">{metric.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="space-y-24">
              {caseStudies.map((study, index) => (
                <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <div className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                      {study.industry}
                    </div>
                    
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                      {study.title}
                    </h2>
                    
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Challenge</h3>
                        <p className="text-gray-600">{study.challenge}</p>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Solution</h3>
                        <p className="text-gray-600">{study.solution}</p>
                      </div>
                      
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Results</h3>
                        <ul className="space-y-2">
                          {study.results.map((result, resultIndex) => (
                            <li key={resultIndex} className="flex items-center space-x-3">
                              <div className="w-5 h-5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                                <i className="ri-check-line text-white text-xs"></i>
                              </div>
                              <span className="text-gray-700 font-medium">{result}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="bg-gray-50 rounded-xl p-6">
                        <blockquote className="text-gray-700 italic mb-3">
                          "{study.testimonial}"
                        </blockquote>
                        <cite className="text-gray-900 font-medium">— {study.author}</cite>
                      </div>
                    </div>
                  </div>
                  
                  <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                    <img 
                      src={study.image}
                      alt={study.title}
                      className="w-full h-96 object-cover rounded-2xl shadow-lg"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Proven Process</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Every success story follows our systematic approach to A.I. implementation.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                {
                  step: '01',
                  title: 'Discovery',
                  description: 'We analyze your current processes and identify automation opportunities.'
                },
                {
                  step: '02',
                  title: 'Strategy',
                  description: 'Custom A.I. strategy designed specifically for your business goals.'
                },
                {
                  step: '03',
                  title: 'Implementation',
                  description: 'Seamless deployment with minimal disruption to your operations.'
                },
                {
                  step: '04',
                  title: 'Optimization',
                  description: 'Continuous monitoring and improvement for maximum ROI.'
                }
              ].map((process, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                  <p className="text-gray-600">{process.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-r from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Join the hundreds of businesses already transforming their operations with A.I. automation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" href="/contact">
                Start Your Transformation
              </Button>
              <Button variant="outline" size="lg" href="/services" className="border-gray-400 text-gray-300 hover:border-white hover:text-white">
                Explore Services
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
